import { Account } from "@/entities/auth/Account";
import { Group } from "@/entities/questionaire/Group";
import Axios, { AxiosRequestConfig, AxiosResponse } from "axios";
import download from "downloadjs";
import { DiseaseParametersModel } from "@/entities/diseaseParameters/DiseaseParametersModel";
import { ToolOpinion } from "@/entities/ToolOpinion";
import { Recommendation } from "@/entities/questionaire/Recommendation";
import { Voting } from "@/entities/voting/Voting";
import { ToolVotingDetail } from "@/entities/voting/ToolVotingDetail";
import { Page } from "@/entities/Page";
import { QuestionVotingDetail } from "@/entities/voting/QuestionVotingDetail";
import { VotingType } from "@/entities/voting/VotingType";
import { VotesType } from "@/entities/voting/VotesType";
import { NewToolVotingModel } from "@/entities/voting/NewToolVotingModel";
import { NewAttributeVotingModel } from "@/entities/voting/NewAttributeVotingModel";
import { SelfcheckReport } from "@/entities/SelfcheckReport";
import { QuestionnaireResult } from "@/entities/questionaire/QuestionnaireResult";
import { SpecialNote } from "@/entities/questionaire/SpecialNote";
import { Branch } from "@/entities/Branch";
import { Settings } from "@/entities/admin/Settings";
import { DiseaseTree } from "@/entities/diseaseParameters/DiseaseTree";
import { ToolOpinionModel } from "@/entities/ToolOpinionModel";
import { Treatment } from "@/entities/Treatment";
import {TnmModel, TnmModelTest, TypeTreeModel} from "@/entities/TnmModel";
import { CompletenessStatistic } from "@/entities/CompletenessStatistic";
import { TnmMappingModel } from "@/entities/admin/TnmMappingModel";
import { TnmGroupModel } from "@/entities/admin/TnmGroupModel";
import { UserService } from "@/services/userService";
import linkGovStore from '../store/linkGovStore'; 
import {  RootStoreState } from '../store/RootStoreState';
import { Links, Publication } from '@/entities/questionaire/Links';

import { Store } from 'vuex';

const protocol: string = "https";
const hostname: string = "localhost";
const port: number = 44397;
const baseUrl: string = process.env.VUE_APP_API_BASE_URL!;

const urls = {
  questionGroup: `${baseUrl}/api/QuestionGroup/get`,
  groups: `${baseUrl}/api/Quiz/get/branchId=`,
  quizResult: `${baseUrl}/api/Quiz/post/branchId={branchId}/useOnlyMyOpinion={useOnlyMyOpinion}&safetyFirst={safetyFirst}`,
  login: `${baseUrl}/api/Account/signin`,
  loginWithKey: `${baseUrl}/api/account/signin-with-key`,
  signup: `${baseUrl}/api/Account/user-signup`,
  forgotPassword: `${baseUrl}/api/Account/forgot-password`,
  resetPassword: `${baseUrl}/api/Account/reset-password`,
  verifyEmail: `${baseUrl}/api/Account/verify-email`,
  validateToken: `${baseUrl}/api/Account/validate-token`,
  getBranchById: `${baseUrl}/api/Branches/getbyid/id={id}`,
  getBranchIdByDiseaseParams: `${baseUrl}/api/Branches/getbyparams`,
  getToolsByBranchId: `${baseUrl}/api/Tool/get/branchId=`,
  getToolOpinionByBranchIdAndToolId: `${baseUrl}/api/Opinion/get/branchId={branchId}&toolId={toolId}`,
  postToolOpinionByBranchIdAndToolId: `${baseUrl}/api/Opinion/post/branchId={branchId}&toolId={toolId}`,
  getVotingTable: `${baseUrl}/api/Voting/get/page={page}&itemsPerPage={itemsPerPage}`,
  getVotingDetailById: `${baseUrl}/api/Voting/get/votingId={votingId}`,
  postUserVoteByVotingId: `${baseUrl}/api/Vote/post/votingId={votingId}&type={type}`,
  postVoting: `${baseUrl}/api/Voting/post`,
  postSelfcheckReport: `${baseUrl}/api/Selfcheck/report/post`,
  getSettings: `${baseUrl}/api/Settings/get`,
  postSettings: `${baseUrl}/api/Settings/post`,
  getDiseaseTree: `${baseUrl}/api/Branches/gettree`,
  importTreeUrl: `${baseUrl}/api/Branches/importtree`,
  importBranchUrl: `${baseUrl}/api/Branches/importbranch/branchId={branchId}`,
  exportTree: `${baseUrl}/api/Branches/exporttree`,
  exportBranch: `${baseUrl}/api/Branches/exportbranch/branchId={branchId}`,
  adminStatistic: `${baseUrl}/api/branches/statistics`,
  expertStatistic: `${baseUrl}/api/branches/statistics/expert`,
  ratingsUrl: `${baseUrl}/api/Opinion/rating/branchId={branchId}`,
  publishVersionUrl: `${baseUrl}/api/branches/version/create`,
  downloadAssessmentResult: `${baseUrl}/api/Quiz/downloadAssessmentResult`,
  saveDependentQuestionSettings: `${baseUrl}/api/Quiz/saveDependentQuestionSettings/branchId=`,
  questionnaireStart: `${baseUrl}/api/questionnaire/start`,
  questionnaireComplete: `${baseUrl}/api/questionnaire/{identifier}/complete`,
  submitSurvey: `${baseUrl}/api/survey`
};

const adminUrls = {
  getUsers: `${baseUrl}/api/admin/users`,
  getUserStats: `${baseUrl}/api/admin/users/stats`,
  updateUserStatus: `${baseUrl}/api/admin/users/{userId}/status/{isActive}`, 
  resendUserVerification: `${baseUrl}/api/admin/users/{userId}/resend-verification`,
  AddUser: `${baseUrl}/api/admin/users`,
  getAdminQuestionnaires: `${baseUrl}/api/adminquestionnaire`
};

export class HttpHandler {
  constructor(private store: Store<RootStoreState>) {}
  private treatnumber: number = 0;
  private id: number = 0;
  
  private static readonly EXCLUDED_QUESTION_SUBTYPES = [
    "HowMany - SelectOne Range",
    "HowMany - YesNo"
  ];

  async getQuestionGroups(): Promise<Group[]> {
    return await Axios.get<Group[]>(urls.questionGroup).then(
      response => response.data
    );
  }

  async getGroupsByBranchId(id: number, isDependentQuestionSettings: boolean = false): Promise<Group[]> {
    const result = await Axios.get(urls.groups + id, { params: { isDependentQuestionSettings } }).then(
      response => response.data.groups
    );

    return result.map((x: any) => Group.createGroup(x));
  }

  async resendVerification(accountId: number): Promise<any> {
    const resendData = {
      accountId: accountId
    };

    const response = await Axios.post(`${baseUrl}/api/Account/resend-verification`, resendData).then(
      response => response.data
    );

    return response;
  }

  private async filterAnswersForAPI(branchId: number, answers: any[]): Promise<any[]> {
    try {
      const groups = await this.getGroupsByBranchId(branchId);
      const questionSubTypeMap = new Map<number, string>();
      
      groups.forEach((group: any) => {
        if (group.properties && Array.isArray(group.properties)) {
          group.properties.forEach((property: any) => {
            questionSubTypeMap.set(
              property.id, 
              property.questionSubType || ''
            );
          });
        }
      });
      
      const filteredAnswers = answers.filter(answer => {
        const questionSubType = questionSubTypeMap.get(answer.propertyId);
        const shouldExclude = HttpHandler.EXCLUDED_QUESTION_SUBTYPES.includes(
          questionSubType || ''
        );
        
        if (shouldExclude) {
          console.log(`Excluding answer for question ${answer.propertyId} with subtype: ${questionSubType}`);
        }
        
        return !shouldExclude;
      });
      
      console.log(`Filtered ${answers.length - filteredAnswers.length} answers with excluded subtypes`);
      console.log('Original answers count:', answers.length);
      console.log('Filtered answers count:', filteredAnswers.length);
      
      return filteredAnswers;
      
    } catch (error) {
      console.error('Error filtering answers for API:', error);
      return answers;
    }
  }

  async postQuizResultObjectsByBranchId(
    branchId: number,
    quizResults: any,
    useOnlyMyOpinion: boolean | null,
    safetyFirst: boolean | null,
    questionnaireIdentifier?: string
  ): Promise<QuestionnaireResult> {

    // console.log('=== postQuizResultObjectsByBranchId DEBUG ===');
    // console.log('branchId:', branchId);
    // console.log('useOnlyMyOpinion:', useOnlyMyOpinion);
    // console.log('safetyFirst:', safetyFirst);
    // console.log('questionnaireIdentifier:', questionnaireIdentifier);
    // console.log('answers count:', quizResults.answers?.length || 0);

    let filteredQuizResults = { ...quizResults };
    localStorage.setItem('currentBranchId', branchId.toString());
    if (quizResults.answers && quizResults.answers.length > 0) {
      const filteredAnswers = await this.filterAnswersForAPI(branchId, quizResults.answers);
      filteredQuizResults.answers = filteredAnswers;
    }
    
    const url = urls.quizResult
      .replace("{branchId}", String(branchId))
      .replace("{useOnlyMyOpinion}", String(useOnlyMyOpinion ?? false))
      .replace("{safetyFirst}", String(safetyFirst ?? false));
     
    //console.log('Calling quiz results API:', url);

    const obj = await Axios.post(url, filteredQuizResults).then((response: AxiosResponse<any>) => {
      return response.data;
    });

    // Call complete API after each question is answered
    if (questionnaireIdentifier) {
      try {
        await this.completeQuestionnaire(questionnaireIdentifier, {
          useOnlyMyOpinion: useOnlyMyOpinion,
          safetyFirst: safetyFirst,
          branchId: branchId,
          answeredQuestionCount: filteredQuizResults.answers?.length || 0,
          completedAction: 'question_answered',
          completedAt: new Date().toISOString()
        });
      } catch (error) {
        console.error('Error calling complete API after question:', error);
      }
    }

    let possibleTreatments: number | null = null;
   
    if (obj.recommendations != null && obj.recommendations.length > 0) {
      possibleTreatments = obj.recommendations[0].possibleTreatments;
      this.store.commit('setShowCardComponents', true);
    } else {
      this.store.commit('setShowCardComponents', false);
    }
    
   // console.log(`Question ${quizResults.answers.length}: Last Answer - Property ID: ${quizResults.answers && quizResults.answers.length > 0 ? quizResults.answers[quizResults.answers.length - 1].propertyId : 'N/A'}, Possible Treatments: ${possibleTreatments}`);
   
    const lastAnswerPropertyId = quizResults.answers && quizResults.answers.length > 0 
      ? quizResults.answers[quizResults.answers.length - 1].propertyId : null;
     
    if (lastAnswerPropertyId !== null && possibleTreatments !== null) {
      this.store.dispatch('localQuestionsTreatment/updateTreatmentById', { 
        id: lastAnswerPropertyId, 
        treatment: possibleTreatments 
      });
    }
    
    const recommendations: Recommendation[] = [];

    if (this.store.getters.getDeleteStore == true) {
      this.store.dispatch('clearAllTreatments');
    } else {
      this.store.commit('setDeleteStore', true);
    }

    if (obj.recommendations != null && obj.recommendations.length > 0) {
      for (let i = 0; i < obj.recommendations.length; i++) {
        const recommendation = obj.recommendations[i];
        const linksData = recommendation.links || []; 
        const publications: Publication[] = [];
        
        if (linksData.length > 0) {
          for (let j = 0; j < linksData.length; j++) {
            const linkItem = linksData[j];
            publications.push(new Publication(linkItem.id, linkItem.name, linkItem.link));
          }
        }

        const smpcLink = recommendation.smpcLink || "";
        const links = new Links(smpcLink, publications);
        
        const tool = new ToolOpinion(
          recommendation.tool.id,
          recommendation.tool.name,
          false,
          [],
          0,
          0,
          [],
          "",
          [],
          "",
          [],
          "",
          null,
          "",
          null,
          "",
          null,
          ""
        );
        tool.toolBranchId = recommendation.toolBranchId;
        this.id = recommendation.tool.id;
        this.treatnumber = recommendation.approvalPercentage;
      
        this.store.dispatch('updateTreatmentById', {
          id: recommendation.tool.id,
          name: recommendation.tool.name, 
          percent: recommendation.approvalPercentage,
          notes: [],
          possibleTreatments: possibleTreatments,
          links: links,
          toolBranchId: recommendation.toolBranchId
        });
        
        recommendations.push(
          new Recommendation(tool, recommendation.approvalPercentage)
        );
      }
    }

    const specialNotes: SpecialNote[] = [];
    if (obj.specialNotes != null && obj.specialNotes.length > 0) {
      for (let i = 0; i < obj.specialNotes.length; i++) {
        const note = obj.specialNotes[i];
        specialNotes.push(new SpecialNote(note.toolName, note.notes));

        const treatmentId = this.store.getters.getTreatmentIdByName(note.toolName);
        if (treatmentId) {
          this.store.dispatch('updateTreatmentNotesById', { 
            id: treatmentId, 
            notes: note.notes 
          });
        } else {
          console.log("SpecialNotes could not assigned!");
        }
      }
    }

    return new QuestionnaireResult(recommendations, specialNotes);
  }

  async validateToken(token: string, tokenType: string = "Reset"): Promise<any> {
    console.log('Validate token URL:', urls.validateToken);
    console.log('Token:', token, 'Type:', tokenType);
    
    const response = await Axios.post(urls.validateToken, {
      token,
      tokenType
    }).then(response => response.data);
    
    return response;
  }

  async getToolOpinionByBranchIdAndToolId(
    branchId: number | null,
    toolId: number | null
  ): Promise<ToolOpinion | null> {
    if (branchId == null || toolId == null) return null;
    const url = urls.getToolOpinionByBranchIdAndToolId
      .replace("{branchId}", String(branchId))
      .replace("{toolId}", String(toolId));

    const result = await Axios.get(url).then(response => response.data);

    const groups = result.groups
      ? result.groups.map((x: any) => Group.createGroup(x))
      : [];

    const otherRatings = result.otherRatings
      ? result.otherRatings.map(
          (x: any) =>
            new ToolOpinionModel(
              { id: x.tool.id, name: x.tool.name },
              x.uniqueColor,
              x.safety,
              x.efficacy
            )
        )
      : [];

    console.log("res", result);
    return new ToolOpinion(
      -1,
      "",
      result.isExist,
      groups,
      result.safety,
      result.efficacy,
      result.mustBeGivenIn,
      result.mustBeGivenInHint,
      result.mustNotBeGivenAfter,
      result.mustNotBeGivenAfterHint,
      result.mustBeGivenAfter,
      result.mustBeGivenAfterHint,
      result.hasToBeTolerated,
      result.hasToBeToleratedHint,
      result.hasToBeEffective,
      result.hasToBeEffectiveHint,
      result.rechallengePossibleAfter,
      result.rechallengePossibleAfterHint,
      result.ignoreTool,
      otherRatings
    );
  }

  async getToolMatrix(
    tumour: string | null = null,
    node: string | null = null,
    metastases: string | null = null
  ): Promise<TnmModelTest[]> {
    const url = `${baseUrl}/api/branches/getMatrix`;
    return await Axios.get(url, { params: { tumour, node, metastases } }).then(
      response => response.data
    );
  }

  async getTypeTree(id: string): Promise<TypeTreeModel> {
    const url = `${baseUrl}/api/branches/getTypeTree`;
    return await Axios.get(url, { params: { id } }).then(
      response => response.data
    );
  }

  async getToolMatrixResultByStage(stage: string): Promise<TnmModel> {
    const url = `${baseUrl}/api/Branches/result`;
    return await Axios.get(url, { params: { stageName: stage } }).then(
      response => response.data
    );
  }

  async postToolOpinionByBranchIdAndToolId(
    branchId: number | null,
    toolId: number | null,
    tool: ToolOpinion | null
  ): Promise<void> {
    if (branchId == null || toolId == null || tool == null) return;
    const url = urls.postToolOpinionByBranchIdAndToolId
      .replace("{branchId}", String(branchId))
      .replace("{toolId}", String(toolId));

    await Axios.post(url, tool.toOpinionModel()).then(response => {
      return response.data;
    });
  }

  async login(email: string, password: string): Promise<Account> {
    let resp = await Axios.post(urls.login, {  email, password }).then(
      response => response.data
    );

    return new Account(
      resp.id,
      resp.login,
      resp.role,
      resp.jwtToken,
      resp.name,
      resp.surname,
      resp.fillSurvey
    );
  }

  async resetPassword(token: string, newPassword: string, confirmPassword: string): Promise<any> {
    console.log('Reset password URL:', urls.resetPassword);
    
    const response = await Axios.post(urls.resetPassword, {
      token,
      newPassword,
      confirmPassword
    }).then(response => response.data);
    
    return response;
  }

  async forgotPassword(email: string): Promise<any> {
    console.log('Forgot password URL:', urls.forgotPassword);
    console.log('Email:', email);
    
    const response = await Axios.post(urls.forgotPassword, { email }).then(
      response => response.data
    );
    
    return response;
  }

  async verifyEmail(token: string): Promise<any> {
    console.log('Verify email URL:', urls.verifyEmail);
    console.log('Token:', token);
    
    const response = await Axios.post(urls.verifyEmail, { token }).then(
      response => response.data
    );
    
    return response;
  }

  async startQuestionnaire(branchId: number, doctorType: string = 'ALL_COMBINED'): Promise<any> {
    const requestBody = {
      branchId: branchId,
      doctorType: doctorType
    };

    try {
      const response = await Axios.post(urls.questionnaireStart, requestBody).then(
        response => response.data
      );
      
      console.log('Questionnaire started successfully:', response);
      return response;
    } catch (error) {
      console.error('Error starting questionnaire:', error);
      throw error;
    }
  }

  async completeQuestionnaire(identifier: string, additionalData?: any): Promise<any> {
    const url = urls.questionnaireComplete.replace("{identifier}", identifier);
    
    const requestBody = {
      ...additionalData
    };
  
    try {
      const response = await Axios.post(url, requestBody).then(
        response => response.data
      );
      
      //console.log('Questionnaire completed successfully:', response);
      return response;
    } catch (error) {
      console.error('Error completing questionnaire:', error);
      throw error;
    }
  }

  async loginWithKey(privateKey: string): Promise<Account> {
    let resp = await Axios.post(urls.loginWithKey, { privateKey }).then(
      response => response.data
    );

    return new Account(
      resp.id,
      resp.login,
      resp.role,
      resp.jwtToken,
      resp.name,
      resp.surname
    );
  }

  async signup(
    firstName: string,
    lastName: string,
    email: string,
    phone: string,
    company: string,
    referrer: number,
    password: string
  ): Promise<any> {
    const signupData = {
      firstName,
      lastName,
      email,
      phone,
      companyName: company,
      referrerSource: referrer,
      password,
      confirmPassword: password
    };
  
    const response = await Axios.post(urls.signup, signupData).then(
      response => response.data
    );
  
    return response;
  }

  private mapReferrerToNumber(referrer: string): number {
    const referrerMap: { [key: string]: number } = {
      'website': 1,
      'word-of-mouth': 2,
      'social-media': 3,
      'conference': 4,
      'colleague': 5,
      'search-engine': 6,
      'advertisement': 7,
      'other': 8
    };
    
    return referrerMap[referrer] || 8;
  }

  async getBranchById(id: number): Promise<Branch> {
    const url = urls.getBranchById.replace("{id}", id.toString());
  
    const obj = await Axios.get(url).then(response => response.data);
  
    const model = new DiseaseParametersModel(
      obj.cancerType,
      obj.cancerStage,
      obj.molecularAlteration,
      obj.alterationType,
      obj.pdL1Status
    );
  
    linkGovStore.commit('SET_OBJECT_ID', obj.id); 
    linkGovStore.commit('SET_OBJECT_NAME', obj.name.toLowerCase()); 
  
    return new Branch(obj.id, obj.name, model, obj.departmentType); // Add departmentType here
  }

  async getBranchIdByDiseaseParams(
    diseaseParams: DiseaseParametersModel
  ): Promise<number> {
    const result = await Axios.post(
      urls.getBranchIdByDiseaseParams,
      diseaseParams.toRequestViewModel()
    ).then(response => response.data);

    return result.id;
  }

  async getToolsByBranchId(branchId: number, startQuestionnaire: boolean = false): Promise<Treatment[]> {
    if (startQuestionnaire) {
      try {
        await this.startQuestionnaire(branchId);
        console.log(`Questionnaire started for branchId: ${branchId}`);
      } catch (error) {
        console.error('Failed to start questionnaire, continuing with tools fetch:', error);
      }
    }

    const toolObjects = await Axios.get(
      urls.getToolsByBranchId + branchId
    ).then(response => response.data);

    return toolObjects.map(
      (treat: any) => new Treatment(treat.id, treat.name, treat.ignoreTool)
    );
  }

  async getVotingTable(page: number): Promise<Page<Voting>> {
    const url = urls.getVotingTable
      .replace("{page}", page.toString())
      .replace("{itemsPerPage}", Page.pageSize.toString());

    const votingObj = await Axios.get(url).then(response => response.data);

    if (
      votingObj == null ||
      votingObj.votings == null ||
      votingObj.votings.length === 0
    ) {
      return new Page<Voting>([], 0, 1);
    }

    const result: Voting[] = [];
    for (let i = 0; i < votingObj.votings.length; i++) {
      const obj = votingObj.votings[i];
      result.push(Voting.createVoting(obj));
    }

    return new Page<Voting>(result, votingObj.pagesCount, 1);
  }

  async getVotingDetailById(
    votingId: number
  ): Promise<ToolVotingDetail | QuestionVotingDetail> {
    const obj = await Axios.get(
      urls.getVotingDetailById.replace("{votingId}", votingId.toString())
    ).then(response => response.data);

    let result: ToolVotingDetail | QuestionVotingDetail;
    if (obj.type === VotingType.addTool) {
      result = ToolVotingDetail.createVotingDetail(obj);
    } else {
      result = QuestionVotingDetail.createVotingDetail(obj);
    }

    return result;
  }

  async postUserVoteByVotingId(votingId: number, userVote: VotesType) {
    if (votingId <= 0 || userVote === VotesType.ignored) {
      return;
    }

    const url = urls.postUserVoteByVotingId
      .replace("{votingId}", votingId.toString())
      .replace("{type}", userVote);

    await Axios.post(url, { votingId: votingId, type: userVote }).then(
      response => {
        return response.data;
      }
    );
  }

  async postNewToolVoting(model: NewToolVotingModel | NewAttributeVotingModel) {
    await Axios.post(urls.postVoting, model).then(response => response.data);
  }

  async postSelfCheckReport(model: SelfcheckReport) {
    await Axios.post(urls.postSelfcheckReport, {
      branchId: model.branchId,
      useOnlyMyOpinion: model.useOnlyMyOpinion,
      answers: model.answers.map(x => x.getQuizResultObject()),
      treatmentHistory: model.treatmentHistory,
      excludedRecommendations: model.excludedRecommendations?.map((e: any) => e.id),
      comment: model.comment
    }).then(response => response.data);
  }

  async saveDependentQuestionSettings(branchId: number, model: any) {
    await Axios.post(urls.saveDependentQuestionSettings + branchId, model).then(response => response.data);
  }

  async downloadAssessmentResult(model: SelfcheckReport) {
    const account = UserService.getAccount();
    const authorizationHeader = `Bearer ${account?.token}`;

    const requestBody = {
        branchId: model.branchId,
        useOnlyMyOpinion: model.useOnlyMyOpinion,
        answers: model.answers.map(x => x.getQuizResultObject()),
        treatmentHistory: model.treatmentHistory,
        excludedRecommendations: model.excludedRecommendations?.map((e: any) => e.id),
        comment: model.comment
    };

    console.log('Request Body:', requestBody);

    await fetch(urls.downloadAssessmentResult, {
        method: 'POST',
        headers: {
            'Authorization': authorizationHeader,
            'Content-Type': 'application/json-patch+json'
        },
        body: JSON.stringify(requestBody)
    })
    .then(async (response) => {
        if (response.ok) {
            const filename = response.headers.get('Content-Disposition')!.split('filename=')[1].slice(0, 38);
            const blob = await response.blob();
            const link = document.createElement('a');
            link.href = window.URL.createObjectURL(blob);
            link.download = filename;
            link.click();
        } else {
            throw new Error(response.statusText);
        }
    })
    .catch((error) => {
        throw new Error(error);
    });
  }

  async getSettings(): Promise<Settings> {
    return Axios.get<Settings>(urls.getSettings).then(
      response => response.data
    );
  }

  async postSettings(settings: Settings): Promise<void> {
    await Axios.post(urls.postSettings, settings).then(
      response => response.data
    );
  }

  async getDiseaseTree(onlyAnswered: boolean = false): Promise<DiseaseTree> {
    const url = `${urls.getDiseaseTree}?onlyAnswered=${onlyAnswered}`;
    const obj = await Axios.get(url).then(response => response.data);

    return DiseaseTree.createFromObject(obj.tree);
  }

  importTreeUrl(): string {
    return urls.importTreeUrl;
  }

  importBranchUrl(branchId: number): string {
    return urls.importBranchUrl.replace("{branchId}", branchId.toString());
  }

  async downloadFile(url: string, fileName: string): Promise<void> {
    let config: AxiosRequestConfig = {
      responseType: "arraybuffer",
      headers: {
        "content-type": "application/vnd.ms-excel;charset=UTF-8"
      }
    };

    const resp = await Axios.post(url, null, config).then(response => response);

    const blob = new Blob([resp.data]);
    download(blob, `${fileName}`, "application/vnd.ms-excel");
  }

  async exportTree(): Promise<void> {
    await this.downloadFile(urls.exportTree, "tree.xlsx");
  }

  async exportTools(branchId: number): Promise<void> {
    const url = urls.exportBranch.replace("{branchId}", branchId.toString());
    await this.downloadFile(url, "branch.xlsx");
  }

  async getAdminStatistic(groupBy: string): Promise<CompletenessStatistic[]> {
    let url = `${urls.adminStatistic}?groupBy=${groupBy}`;
    return Axios.get<CompletenessStatistic[]>(url).then(
      response => response.data
    );
  }

  async getExpertStatistic(groupBy: string): Promise<CompletenessStatistic[]> {
    let url = `${urls.expertStatistic}?groupBy=${groupBy}`;
    return Axios.get<CompletenessStatistic[]>(url).then(
      response => response.data
    );
  }

  async getRatings(branchId: number | null): Promise<ToolOpinionModel[]> {
    if (branchId == null) return [];

    const url = urls.ratingsUrl.replace("{branchId}", String(branchId));
    return await Axios.get<ToolOpinionModel[]>(url).then(response =>
      response.data.map(
        model =>
          new ToolOpinionModel(
            model.tool,
            model.uniqueColor,
            model.safety,
            model.efficacy
          )
      )
    );
  }

  async setRating(branchId: number | null, data: any): Promise<void> {
    if (branchId == null) return;

    const url = urls.ratingsUrl.replace("{branchId}", String(branchId));
    await Axios.post(url, data).then(response => {
      return response.data;
    });
  }

  async publishVersion(): Promise<void> {
    return await Axios.post(urls.publishVersionUrl);
  }

  async importData(
    baseUrl: string,
    formData: any,
    force: boolean | null = null
  ) {
    let url = `${baseUrl}`;
    if (force !== null) {
      url = `${url}&force=${force}`;
    }

    return await Axios.post(url, formData).then(response => response.data);
  }

  async getTnmMapping(): Promise<TnmMappingModel[]> {
    const url = `${baseUrl}/api/branches/getTnmMappings`;
    return Axios.get<TnmMappingModel[]>(url).then(response => response.data);
  }

  async getTnmGroups(): Promise<TnmGroupModel> {
    const url = `${baseUrl}/api/branches/getTnmMappingVariants`;
    return Axios.get<TnmGroupModel>(url).then(response => response.data);
  }

  async setTnmMapping(
    tumour: string,
    node: string,
    metastases: string,
    type: string,
    groupingId: string
  ): Promise<any> {
    const body = {
      tumour,
      node,
      metastases,
      type,
      groupingId
    };
    const url = `${baseUrl}/api/branches/setTnmMappingVariant`;
    return Axios.post(url, body).then(response => response.data);
  }

  async submitSurvey(surveyData: {
    recentBranchUsedId: number | null;
    wasHelpful: boolean;
    wouldUseAgain: boolean;
    recommendationScore: number;
    comments: string;
  }): Promise<any> {
    try {
      // Get the current branchId from localStorage
      const currentBranchId = localStorage.getItem('currentBranchId');
      const branchId = currentBranchId ? parseInt(currentBranchId) : null;
      
      // Use the branchId from localStorage if surveyData doesn't have one
      const dataToSubmit = {
        ...surveyData,
        recentBranchUsedId: surveyData.recentBranchUsedId || branchId
      };
      
      console.log('Submitting survey to API:', dataToSubmit);
      
      const response = await Axios.post(urls.submitSurvey, dataToSubmit).then(
        response => response.data
      );
      
      console.log('Survey submission response:', response);
      return response;
    } catch (error) {
      console.error('Error submitting survey:', error);
      throw error;
    }
  }
  

  // async getAdminUsers(filters: any = {}): Promise<any> {
  //   const queryParams = new URLSearchParams();
    
  //   Object.entries(filters).forEach(([key, value]) => {
  //     if (value !== null && value !== undefined && value !== '') {
  //       queryParams.append(key, value.toString());
  //     }
  //   });
  
  //   const queryString = queryParams.toString();
  //   const url = queryString ? `${adminUrls.getUsers}?${queryString}` : adminUrls.getUsers;
    
  //   console.log('Admin Users API Call:', url);
    
  //   try {
  //     const response = await Axios.get(url);
  //     return response.data;
  //   } catch (error) {
  //     console.error('Error fetching admin users:', error);
  //     throw error;
  //   }
  // }
  async getAdminUsers(filters: any = {}): Promise<any> {
    // Merge default filters with provided filters
    const mergedFilters = {
      isActive: true,
      ...filters
    };
  
    const queryParams = new URLSearchParams();
    
    Object.entries(mergedFilters).forEach(([key, value]) => {
      if (value !== null && value !== undefined && value !== '') {
        queryParams.append(key, value.toString());
      }
    });
  
    const queryString = queryParams.toString();
    const url = queryString ? `${adminUrls.getUsers}?${queryString}` : adminUrls.getUsers;
    
    console.log('Admin Users API Call:', url);
    
    try {
      const response = await Axios.get(url);
      return response.data;
    } catch (error) {
      console.error('Error fetching admin users:', error);
      throw error;
    }
  }
  
  async getAdminUserStats(): Promise<any> {
    try {
      const response = await Axios.get(adminUrls.getUserStats);
      return response.data;
    } catch (error) {
      console.error('Error fetching user stats:', error);
      throw error;
    }
  }
  
  async updateAdminUserStatus(userId: number, isActive: boolean): Promise<any> {
    const url = adminUrls.updateUserStatus
      .replace('{userId}', userId.toString())
      .replace('{isActive}', isActive.toString());  // ✅ ADD THIS LINE
    
    try {
      const response = await Axios.patch(url);  // ✅ Remove payload
      return response.data;
    } catch (error) {
      console.error('Error updating user status:', error);
      throw error;
    }
  }
  
  async resendAdminUserVerification(userId: number): Promise<any> {
    const url = adminUrls.resendUserVerification.replace('{userId}', userId.toString());
    
    try {
      const response = await Axios.post(url);
      return response.data;
    } catch (error) {
      console.error('Error resending verification:', error);
      throw error;
    }
  }

  async addAdminUser(userData: {
    firstName: string;
    lastName: string;
    email: string;
    phone?: string;
    companyName?: string;
    referrerSource?: string | number;
    role: string;
  }): Promise<any> {
    const requestBody = {
      firstName: userData.firstName,
      lastName: userData.lastName,
      email: userData.email,
      phone: userData.phone || '',
      companyName: userData.companyName || '',
      referrerSource: typeof userData.referrerSource === 'string' 
        ? parseInt(userData.referrerSource) || 1 
        : userData.referrerSource || 1,
      role: userData.role
    };
    const url = adminUrls.AddUser;
    try {
      const response = await Axios.post(url, requestBody);
      return response.data;
    } catch (error: unknown) {
      if (error instanceof Error && 'response' in error) {
        const axiosError = error as any;
        const errorMessage = axiosError.response?.data?.message || 
                            axiosError.response?.data?.error || 
                            `Server error: ${axiosError.response?.status}`;
        throw new Error(errorMessage);
      }
      throw new Error('Failed to add user');
    }
  }

  async getAdminQuestionnaires(filters: any = {}): Promise<any> {
    const queryParams = new URLSearchParams();
    
    if (filters.search && filters.search.trim() !== '') {
      // Search will be handled on frontend after fetching data
    }
    if (filters.branchId && filters.branchId !== '') {
      queryParams.append('branchId', filters.branchId);
    }
    if (filters.givenBy && filters.givenBy !== '') {
      queryParams.append('givenBy', filters.givenBy);
    }
    if (filters.dateRange && filters.dateRange !== '') {
      queryParams.append('testGiven', filters.dateRange);
    }
    
    const queryString = queryParams.toString();
    const url = queryString ? `${adminUrls.getAdminQuestionnaires}?${queryString}` : adminUrls.getAdminQuestionnaires;
    
    console.log('Admin Questionnaires API Call:', url);
    
    try {
      const response = await Axios.get(url);
      return response.data;
    } catch (error) {
      console.error('Error fetching admin questionnaires:', error);
      throw error;
    }
  }
}